import * as React from "react";
import { connect } from "react-redux";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import { SubPageLayout } from "../../../components/layouts/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { FC, useEffect, useMemo } from "react";
import { ChangeOptions, Form, ModelAction, actions } from "react-redux-form";
import * as InstitutionItemActions from "../../../actions/institutionItem";
import { NotificationAlert } from "../../../components/common/index";
import { TextFormInput } from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IInstitutionItemReduxFormState } from "../../../reducers/reactReduxForms/institutionItem";

import "./InstitutionItemCreateVersion.scss";

const reduxFormName: string = "reduxForms.institutionItem";

interface InstitutionItemCreateVersionProps {
    currentInstitutionItem: Dtos.InstitutionItem;
    institutionId?: number;
    form?: Dtos.InstitutionItem & IRegistrationFormState;
    formState?: Dtos.FormState;
    formProperties?: Dtos.FormProperty[];
    reduxFormState?: IInstitutionItemReduxFormState;
    institutionItem?: Dtos.InstitutionItem;
    lookups?: Dtos.Lookup[];
    loadingInstitutionItem?: boolean;
    savingInstitutionItem?: boolean;
    creatingInstitutionItem?: boolean;
    createInstitutionItemSuccess?: boolean;
    validationFailures?: Dtos.ResponseError[];
    onClose?: Function;
    clearInstitutionItem?: (propertiesToClear?: string[]) => void;
    saveInstitutionItem?: (
        formData: Dtos.InstitutionItem,
        collectionFormData: Dtos.Collection
    ) => void;
    createInstitutionItem?: (institutionId: number) => void;
    loadForm?: (model: string, value: any) => ModelAction;
    changeForm?: (
        model: string,
        value: any,
        options?: ChangeOptions
    ) => ModelAction;
}

const InstitutionItemCreateVersion: FC<InstitutionItemCreateVersionProps> = ({
    currentInstitutionItem,
    institutionId,
    form,
    formState,
    formProperties,
    institutionItem,
    lookups,
    loadingInstitutionItem,
    savingInstitutionItem,
    creatingInstitutionItem,
    createInstitutionItemSuccess,
    validationFailures,
    onClose,
    clearInstitutionItem,
    saveInstitutionItem,
    createInstitutionItem,
    loadForm,
    changeForm,
}) => {
    // Create InstitutionItem
    useEffect(() => {
        if (!institutionItem && institutionId) {
            createInstitutionItem(institutionId);
        }
    }, [institutionItem, institutionId, createInstitutionItem]);

    // Load Form
    useEffect(() => {
        if (!creatingInstitutionItem && createInstitutionItemSuccess) {
            loadReduxForm();
        }
    }, [creatingInstitutionItem, createInstitutionItemSuccess, loadForm]);

    // Cleanup effect to clear the institution item on component unmount
    useEffect(() => {
        return () => {
            clearInstitutionItem();
        };
    }, [clearInstitutionItem]);

    const handleOnSubmit = (
        data: Dtos.InstitutionItem,
        currentInstitutionItem: Dtos.InstitutionItem
    ) => {
        if (!data.version) {
            handleOnSubmitFailed(data);
            return;
        }

        if (data) {
            const changes: Dtos.InstitutionItem = {
                institutionId: currentInstitutionItem.institutionId,
                institutionItemTemplateId:
                    currentInstitutionItem.institutionItemTemplateId,
                active: true,
            } as Dtos.InstitutionItem;
            saveInstitutionItem({ ...data, ...changes }, null);
        }
        onCancel();
    };

    const handleOnSubmitFailed = (_data: Dtos.InstitutionItem) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        if (onClose) {
            onClose();
        }
    };

    const loadReduxForm = () => {
        let extraData: Dtos.InstitutionItem = {
            version: undefined,
        } as Dtos.InstitutionItem;

        loadForm(
            reduxFormName,
            Object.assign({ ...institutionItem }, extraData)
        );
    };

    const renderContent = useMemo(() => {
        const formDisabled: boolean =
            savingInstitutionItem ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            version: {
                type: RegistrationComponent.RegistrationControl,
                inputType: TextFormInput,
                inputDisabled: formDisabled,
                validators: {
                    required: (value) =>
                        value != undefined &&
                        value != null &&
                        value?.length > 0,
                },
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) =>
                        handleOnSubmit(val, currentInstitutionItem)
                    }
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "institutionItem",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        reduxFormName,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        form,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
        currentInstitutionItem,
    ]);

    return (
        <SubPageLayout loading={loadingInstitutionItem}>
            {renderContent}
        </SubPageLayout>
    );
};

const mapStateToProps = (
    state: ITmdState,
    ownProps: InstitutionItemCreateVersionProps
): Partial<InstitutionItemCreateVersionProps> => {
    let institutionItem: Dtos.InstitutionItem | undefined = !(
        state.institutionItem.formData instanceof Array
    )
        ? state.institutionItem.formData
        : undefined;

    return {
        currentInstitutionItem: ownProps.currentInstitutionItem,
        institutionId: ownProps.institutionId,
        institutionItem: institutionItem!,
        loadingInstitutionItem:
            state.institutionItem.loadState &&
            state.institutionItem.loadState.status == RequestState.Pending,
        savingInstitutionItem:
            state.institutionItem.saveState &&
            state.institutionItem.saveState.status == RequestState.Pending,
        creatingInstitutionItem:
            state.institutionItem.createState &&
            state.institutionItem.createState.status == RequestState.Pending,
        createInstitutionItemSuccess:
            state.institutionItem.createState &&
            state.institutionItem.createState.status == RequestState.Success,
        lookups: state.institutionItem.lookups,
        form: state.reduxForms.institutionItem,
        formState: state.institutionItem.formState,
        formProperties: state.institutionItem.formProperties,
        reduxFormState: state.reduxForms.formState.institutionItem,
        validationFailures: state.institutionItem.validationFailures,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<InstitutionItemCreateVersionProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        clearInstitutionItem: bindActionCreators(
            InstitutionItemActions.Clear,
            dispatch
        ),
        saveInstitutionItem: bindActionCreators(
            InstitutionItemActions.SaveInstitutionItem,
            dispatch
        ),
        createInstitutionItem: bindActionCreators(
            InstitutionItemActions.CreateInstitutionItem,
            dispatch
        ),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(InstitutionItemCreateVersion);
